import React, { useEffect, useState, useMemo } from 'react';
import {
  Form,
  Modal,
  Button,
  Spin,
  Col,
  DatePicker,
  message,
  Typography,
  Dropdown,
  Tooltip,
  Collapse,
  Row,
  Menu,
  Input,
  Tag,
  Grid,
} from 'antd';
import PropTypes from 'prop-types';
import {
  getRequisitosDeTramites,
  deleteRequisitosDeTramites,
  postRequisitosDeTramites,
  patchRequisitosDeTramites,
} from 'api/tramites/requisitosDeTramites';
import FormSubmitCustomControls from 'components/FormSubmitCustomControls';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';
import { DATE_FRONTEND_FORMAT } from 'utils/formatValues';
import { patchTramite } from 'api/tramites/tramites';
import {
  getDocumentosDeTramites,
  patchDocumentoDeTramite,
  postDocumentoDeTramite,
} from 'api/empresas/documentosDeTramites';
import {
  getCategoriasDeRequisitos,
  getManyCatalogosDeRequisitos,
  getManyRequisitos,
  getTiposDeRequisitos,
  getUnidadesDePlazo,
} from 'api/tramites';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  EyeOutlined,
  LinkOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { toMoment, listToString } from 'utils/normalizers';
import styled from 'styled-components';
import { ESTADOS_GLOBALES_MAP, isAuthorized, isMutable } from 'utils/estadosGlobales';
import {
  statusRequisitosEnum,
  solicitantesEnum,
  tiposDeRequisitosEnum,
} from 'views/Tramites/GPM/enums';
import Previewer from 'components/Previewer';
import { useSelector } from 'react-redux';
import { getTipoDePadronPorContenType } from 'api/catalogos';
import NoPermission from './NoPermission';
import Camera from './Camera';

const DEV = process.env.NODE_ENV === 'development';
const REPUVE = process.env.REACT_APP_ENV_NAME === 'repuve';

// TODO: posiblemente cambiar por las actions para definir y segmentar los permisos para la
// accion que corresponden, usar acciones sin permisos como separadores en el render
// de de permisos.jsx para que sean separadores de secciones
export const permissionRequisitos = {
  title: 'Requisitos',
  name: 'requisitodeltramite',
  model: 'requisitodeltramite',
  permissions: [
    'view_requisitodeltramite',
    'add_requisitodeltramite',
    'change_requisitodeltramite',
    'delete_requisitodeltramite',
    'autorizar_requisitodeltramite',
    'view_requisito',
    'view_catalogoderequisitos',
    'view_tipoderequisito',
    'view_categoriaparatramite',
    'view_unidaddeplazo',
    'view_documentodetramite',
    'add_documentodetramite',
    'change_documentodetramite',
  ],
};

export const isInReview = (item) => item?.es_requisito_incompleto
  && item.estados_globales === ESTADOS_GLOBALES_MAP.EN_PROCESO;

const { Text, Title } = Typography;
const now = toMoment(new Date());
const rules = { required: [{ required: true }] };

const mapDocumentos = (doc) => ({
  ...doc,
  fecha_vencimiento: toMoment(doc.fecha_vencimiento),
  vigente: doc.estados_globales === 4
    && doc.fecha_vencimiento ? toMoment(doc.fecha_vencimiento).isAfter(now) : true,
});

const RequisitosDeTramite = ({
  permission,
  casoDeRequisitos,
  tramite,
  setStatusRequisitos,
  setCasoDeRequisitos,
  loading,
  setLoading,
  setShowMessagesRequisitos,
  showMessagesRequisitos,
  requisitosGenerales,
  disabled,
  allowReview,
  hasPermission,
  authorizeAllCallback,
  isLastStep,
  fetchCallback,
}) => {
  const screens = Grid.useBreakpoint();
  const { estadosGlobales, padrones: _padrones } = useSelector((state) => state.catalogs);
  const padrones = _padrones.filter((p) => p.id !== 5);
  const [form] = Form.useForm();
  const tiene_vigencia = Form.useWatch('tiene_vigencia', form);
  const es_documento_digital = Form.useWatch('es_documento_digital', form);
  const requisitoId = Form.useWatch('requisito', form);
  const [requisitosDeTramites, setRequisitosDeTramites] = useState([]);
  const [visible, setVisible] = useState(false);
  const [selectedRequisito, setSelectedRequisito] = useState(null);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [dateVigenciaMaxima, setDateVigenciaMaxima] = useState();

  // Catalogos
  const [tiposDeRequisitos, setTiposDeRequisitos] = useState([]);
  const [catalogosDeRequisitos, setCatalogosDeRequisitos] = useState([]);
  const [categoriasDeRequisitos, setCategoriasDeRequisitos] = useState([]);
  const [unidadesDePlazo, setUnidadesDePlazo] = useState([]);
  const [documentosParaTramites, setDocumentosParaTramites] = useState([]);
  const [requisitos, setRequisitos] = useState([]);
  const [visibleAddComments, setVisibleAddComments] = useState(false);

  const requisitoSelected = requisitos.find((e) => e.id === requisitoId);

  const disabledDate = (date) => date.isBefore(now)
    || (dateVigenciaMaxima && date.isAfter(dateVigenciaMaxima));

  const tipoDePadronSolicitante = tramite.solicitante_ciudadano
    ? solicitantesEnum.CIUDADANO : solicitantesEnum.CONTRIBUYENTE;
  const solicitanteId = tramite.solicitante_ciudadano
    ? tramite.ciudadano : tramite.contribuyente;
  const contentTypeSolicitante = padrones
    .find((p) => p.id === tipoDePadronSolicitante)?.content_type;
  const afectadoId = tramite.object_id_padron;
  const contentTypeAfectado = tramite.content_type_a_padron;
  const tipoDePadronAfectado = tramite.content_type_a_padron
    && getTipoDePadronPorContenType(tramite.content_type_a_padron);

  const fetchData = async (_documentosParaTramites = documentosParaTramites) => {
    setLoading(true);
    const _requisitosDeTramites = await getRequisitosDeTramites({
      tramite: tramite?.id,
    });
    const _requisitos = _requisitosDeTramites.filter((e) => e.estados_globales !== 5).map((r) => ({
      ...r,
      documentos_de_tramite: _documentosParaTramites
        .findLast((e) => e.id === r.documentos_de_tramite?.id) || r.documentos_de_tramite,
    }));
    setRequisitosDeTramites(_requisitos);
    setLoading(false);
  };

  const fetchDocumentosParaTramites = async (
    _set = false,
    _requisitosDeTramites = requisitosDeTramites,
  ) => {
    const promises = [
      getDocumentosDeTramites({
        padron_object_id: solicitanteId,
        padron_content: contentTypeSolicitante,
      }),
    ];
    if (afectadoId
        && (solicitanteId !== afectadoId || contentTypeSolicitante !== contentTypeAfectado)) {
      promises.push(getDocumentosDeTramites({
        padron_object_id: afectadoId,
        padron_content: contentTypeAfectado,
      }));
    }
    const [_documentosSolicitante, _documentosAfectado = []] = await Promise.all(promises);
    const _documentos = [..._documentosSolicitante, ..._documentosAfectado].map(mapDocumentos);
    if (_set) {
      setDocumentosParaTramites(_documentos);
      const _requisitosTramite = _requisitosDeTramites.map((req) => ({
        ...req,
        documentos_de_tramite: _documentos
          .findLast((e) => e.id === req.documentos_de_tramite?.id) || req.documentos_de_tramite,
      }));
      setRequisitosDeTramites(_requisitosTramite);
      await fetchData(_documentos);
    }
    return _documentos;
  };

  useEffect(() => {
    let mounted = true;
    const fetch = async () => {
      setLoading(true);
      const alreadyObjects = (requisitosGenerales && typeof requisitosGenerales[0] === 'object')
        || (casoDeRequisitos?.requisitos && typeof casoDeRequisitos?.requisitos[0] === 'object');
      const _requisitos = alreadyObjects ? casoDeRequisitos?.requisitos || requisitosGenerales
        : await getManyRequisitos(requisitosGenerales || casoDeRequisitos?.requisitos);
      const promises = [
        getRequisitosDeTramites({ tramite: tramite?.id }),
        getTiposDeRequisitos(),
        getCategoriasDeRequisitos(),
        getUnidadesDePlazo(),
        getManyCatalogosDeRequisitos(_requisitos.map((e) => e.catalogo_de_requisitos)),
      ];
      const [
        _requisitosDeTramites,
        _tiposDeRequisitos,
        _categoriasDeRequisitos,
        _unidadesDePlazo,
        _catalogosDeRequisitos,
      ] = await Promise.all(promises);
      await fetchDocumentosParaTramites(true, _requisitosDeTramites);
      if (mounted) {
        setTiposDeRequisitos(_tiposDeRequisitos);
        setCategoriasDeRequisitos(_categoriasDeRequisitos);
        setCatalogosDeRequisitos(_catalogosDeRequisitos);
        setUnidadesDePlazo(_unidadesDePlazo);
        setRequisitos(_requisitos);
        setLoading(false);
      }
    };
    fetch();
    return () => {
      mounted = false;
      setShowMessagesRequisitos?.(false);
    };
  }, []);

  const onCancel = () => {
    setVisible(false);
    form.resetFields();
    setDateVigenciaMaxima();
    setSelectedRequisito();
    setVisibleAddComments(false);
  };

  const postDocumentos = async ([curr, ...rest], data = []) => {
    if (!curr) return data;
    const _data = await postDocumentoDeTramite(curr);
    if (!rest.length) return [...data, _data];
    return postDocumentos(rest, [...data, _data]);
  };

  const onFinish = async (values) => {
    setLoading(true);
    const requisito = requisitos.find((r) => r.id === values.requisito);
    let valuesDocumento = null;
    if (values.archivo?.file || values.archivo?.length) {
      valuesDocumento = {
        requisito: values.requisito,
        fecha_vencimiento: values.fecha_vencimiento,
        estados_globales: 4,
        cancelar_documento_anterior: true,
      };
      const catalogoDeRequisito = catalogosDeRequisitos
        .find((c) => c.id === requisito.catalogo_de_requisitos);
      valuesDocumento.catalogo_de_requisitos = catalogoDeRequisito.id;
      if (catalogoDeRequisito.padron) {
        if (catalogoDeRequisito.padron === tipoDePadronAfectado) {
          valuesDocumento.padron_object_id = afectadoId;
          valuesDocumento.padron_content = contentTypeAfectado;
        } else {
          valuesDocumento.padron_object_id = solicitanteId;
          valuesDocumento.padron_content = contentTypeSolicitante;
        }
      }
    }
    let savedFile = [];
    if (valuesDocumento && values.archivo?.file) {
      valuesDocumento.archivo = values.archivo?.file;
      savedFile = await postDocumentoDeTramite(valuesDocumento);
    } else if (valuesDocumento && values.archivo?.length) {
      savedFile = await postDocumentos(values.archivo.map((e, idx) => ({
        ...valuesDocumento,
        archivo: e.originFileObj,
        cancelar_documento_anterior: idx === 0,
      })));
    }
    if (valuesDocumento && !(savedFile.length || savedFile.id)) {
      message.info('Ah ocurrido un error al registrar el requisito, intentelo de nuevo');
      setLoading(false);
      return;
    }
    const valuesRequisito = {
      estados_globales: values.estados_globales,
      requisito: requisito.id,
      tramite: tramite.id,
      documentos_de_tramite: savedFile?.id ? [savedFile.id] : savedFile.map((e) => e.id),
      fecha_de_vencimiento: values.fecha_vencimiento,
      descripcion: requisito.descripcion + (new Date()).getTime(),
    };
    const saved = await postRequisitosDeTramites(valuesRequisito);
    if (saved) {
      if (!tramite.caso && casoDeRequisitos) {
        const updatedTramite = await patchTramite(tramite.id, { caso: casoDeRequisitos.id });
        if (updatedTramite) {
          setCasoDeRequisitos(casoDeRequisitos.id);
        }
      }
      onCancel();
      if (valuesDocumento) {
        await fetchDocumentosParaTramites(true);
      } else {
        await fetchData();
      }
    }
    setLoading(false);
  };

  const onClickLinkDigital = async (requisito, documentos) => {
    setLoading(true);
    const values = {
      requisito: requisito.id,
      tramite: tramite.id,
      descripcion: requisito.descripcion + (new Date()).getTime(),
      documentos_de_tramite: documentos.map((e) => e.id),
      fecha_de_vencimiento: documentos[0].fecha_vencimiento,
      estados_globales: 2,
    };
    const saved = await postRequisitosDeTramites(values);
    if (saved) {
      onCancel();
      await fetchData();
    }
    setLoading(false);
  };

  const unidadesDePlazoMap = {
    DIAS_HABILES: 1,
    MESES: 2,
    ANIOS: 3,
  };

  const addTimeMap = {
    [unidadesDePlazoMap.DIAS_HABILES]: 'day',
    [unidadesDePlazoMap.MESES]: 'month',
    [unidadesDePlazoMap.ANIOS]: 'year',
  };

  const onClickAdd = (requisito, _es_documento_digital) => {
    onCancel();
    const values = {
      requisito: requisito.id,
      tiene_vigencia: requisito.tiene_vigencia && _es_documento_digital,
      es_documento_digital: _es_documento_digital,
    };
    if (requisito.tiene_vigencia && requisito.vigencia_en_dias && requisito.unidad_de_plazo) {
      const date = toMoment(new Date())
        .add(requisito.vigencia_en_dias, addTimeMap[requisito.unidad_de_plazo]);
      values.fecha_vencimiento = date;
      setDateVigenciaMaxima(date);
    }
    if (_es_documento_digital) {
      form.setFieldsValue(values);
      setVisible(true);
    } else {
      values.estados_globales = 2;
      setTimeout(() => onFinish(values));
    }
  };

  const onClickView = (requisito) => {
    const { requisitoDeTramite: _requisitoDeTramite } = requisito;
    const values = {
      observaciones: _requisitoDeTramite.observaciones,
      es_requisito_incompleto: _requisitoDeTramite.es_requisito_incompleto,
      observaciones_de_incompleto: _requisitoDeTramite.observaciones_de_incompleto,
      es_documento_digital: _requisitoDeTramite.es_documento_digital,
      requisito: requisito.id,
      permite_documento_electronico: requisito.permite_documento_electronico,
      estados_globales: requisito.estados_globales,
      tiene_vigencia: requisito.tiene_vigencia,
      usuario_creo: _requisitoDeTramite.usuario_creo,
      fecha_vencimiento: toMoment(_requisitoDeTramite.fecha_de_vencimiento),
      fecha_de_creacion: toMoment(_requisitoDeTramite.fecha_de_creacion).format(),
    };
    const documento = _requisitoDeTramite.documentos_de_tramite;
    if (documento?.length || documento?.id) {
      const doc = documento.length ? documento[0] : documento;
      values.archivo = doc.archivo;
      values.fecha_vencimiento = toMoment(doc.fecha_vencimiento);
    }
    if (requisito.tiene_vigencia && requisito.vigencia_en_dias
      && requisito.unidad_de_plazo && documento) {
      const date = toMoment(documento.fecha_de_creacion)
        .add(requisito.vigencia_en_dias, addTimeMap[requisito.unidad_de_plazo]);
      values.fecha_vencimiento = date;
      setDateVigenciaMaxima(date);
    }
    form.setFieldsValue(values);
    setSelectedRequisito(requisito.requisitoDeTramite);
    setTimeout(() => setVisible(true), 1000);
  };

  const onClickDelete = (requisito) => {
    setSelectedRequisito(requisito.requisitoDeTramite);
    setVisibleAlert(true);
  };

  const deleteItem = async () => {
    setLoading(true);
    const deleted = await deleteRequisitosDeTramites(selectedRequisito.id);
    if (deleted) {
      setVisibleAlert(false);
      onCancel();
      await fetchData();
    }
    setLoading(false);
  };

  const categorias = useMemo(() => {
    if (!(categoriasDeRequisitos.length && catalogosDeRequisitos.length
      && tiposDeRequisitos.length && requisitos.length)) {
      return [];
    }
    const _categorias = categoriasDeRequisitos.map((categoria) => {
      const catalogos = catalogosDeRequisitos
        .filter((catalogo) => catalogo.categoria === categoria.id).map(({ id }) => id);
      const _requisitos = requisitos
        .filter((requisito) => catalogos.includes(requisito.catalogo_de_requisitos)
          && (!requisito.padron || tipoDePadronSolicitante === requisito.padron
          || tipoDePadronAfectado === requisito.padron))
        .map((requisito) => {
          const catalogo_de_requisitos = catalogosDeRequisitos
            .find((catalogo) => catalogo.id === requisito.catalogo_de_requisitos);
          const tipo_de_requisito = tiposDeRequisitos
            .find((e) => e.id === requisito.tipo_de_requisito);
          // TODO: find related only by category
          const related = tipo_de_requisito.id === tiposDeRequisitosEnum.INCLUSIVO
            ? requisitos.filter((req) => {
              const cat = catalogosDeRequisitos.find((c) => c.id === req.catalogo_de_requisitos);
              return req.tipo_de_requisito === tipo_de_requisito.id
                && cat.categoria === categoria.id
                && req.id !== requisito.id;
            }) : [];
          const relatedUploaded = related.some((r) => requisitosDeTramites
            .some((re) => re.requisito === r.id));
          const _requisitoDeTramite = requisitosDeTramites
            .find((re) => re.requisito === requisito.id);
          const required = tipo_de_requisito.id === tiposDeRequisitosEnum.OBLIGATORIO
            || (tipo_de_requisito.id === tiposDeRequisitosEnum.INCLUSIVO && relatedUploaded)
            || (tipo_de_requisito.id === tiposDeRequisitosEnum.EXCLUSIVO
              && !(relatedUploaded || _requisitoDeTramite));
          return ({
            ...requisito,
            catalogo_de_requisitos,
            tipo_de_requisito,
            required,
            requisitoDeTramite: _requisitoDeTramite,
            related,
          });
        })
        .sort((a, b) => a.catalogo_de_requisitos.numero - b.catalogo_de_requisitos.numero);
      const filterCatalogs = (catalogo) => _requisitos
        .some((r) => r.catalogo_de_requisitos.id === catalogo.id);
      const _catalogos = catalogosDeRequisitos.filter(filterCatalogs).map((_catalogo) => ({
        ..._catalogo,
        requisitos: _requisitos
          .filter((requisito) => requisito.catalogo_de_requisitos.id === _catalogo.id),
      }));
      if (!_requisitos.length) {
        return null;
      }
      const output = {
        ...categoria,
        requisitos: _catalogos.map((e) => e.requisitos).flat().map((requisito) => ({
          documentoDigitalExistente: documentosParaTramites
            .filter((doc) => doc.padron_object_id && doc.vigente
              && doc.catalogo_de_requisitos.id === requisito.catalogo_de_requisitos.id),
          ...requisito,
        })),
      };
      output.groups = tiposDeRequisitos.map((tipo_de_requisito) => {
        const reqs = output.requisitos
          .filter((requisito) => requisito.tipo_de_requisito.id === tipo_de_requisito.id)
          .sort((a, b) => a.catalogo_de_requisitos.numero - b.catalogo_de_requisitos.numero);
        if (!reqs.length) return null;
        return {
          tipo_de_requisito,
          requisitos: reqs,
        };
      }).filter((e) => e);
      return output;
    }).filter((e) => e);
    return _categorias;
  }, [requisitosDeTramites, categoriasDeRequisitos,
    catalogosDeRequisitos, tiposDeRequisitos, requisitos]);

  const getRequisitosRequeridos = () => {
    const groups = categorias.map((categoria) => categoria.groups).flat();
    const groupsExclusivos = groups
      .filter((group) => group.tipo_de_requisito.id === tiposDeRequisitosEnum.EXCLUSIVO);
    const groupsNoExclusivos = groups
      .filter((group) => group.tipo_de_requisito.id !== tiposDeRequisitosEnum.EXCLUSIVO);
    const _requisitosRequeridos = groupsNoExclusivos.map((group) => group.requisitos)
      .flat()
      .filter((e) => e.required);
    groupsExclusivos.forEach((group) => {
      const hasRequisito = group.requisitos.some((requisito) => requisito.requisitoDeTramite);
      if (!hasRequisito) {
        _requisitosRequeridos.push(
          listToString(group.requisitos.map((r) => r.descripcion), 'disjunction'),
        );
      }
    });
    return _requisitosRequeridos;
  };

  const _requisitosDeTramites = requisitosDeTramites
    .filter((rt) => requisitos.some((r) => r.id === rt.requisito));
  const requisitosRequeridos = getRequisitosRequeridos();
  const hasAllRequisitos = requisitosRequeridos.every((e) => e.requisitoDeTramite);
  const someInReviewFuncionario = allowReview && _requisitosDeTramites.some(isInReview);

  const status = useMemo(() => {
    if (!requisitos.length || !hasAllRequisitos || someInReviewFuncionario) {
      return statusRequisitosEnum.INCOMPLETE;
    }
    const readyToauthorize = _requisitosDeTramites
      .every((r) => r.estados_globales === ESTADOS_GLOBALES_MAP.EN_PROCESO
      && !r.es_requisito_incompleto);
    if (readyToauthorize) {
      return statusRequisitosEnum.COMPLETE;
    }
    if (_requisitosDeTramites
      .every((r) => r.estados_globales === ESTADOS_GLOBALES_MAP.AUTORIZADO)) {
      return statusRequisitosEnum.AUTORIZED;
    }
    return statusRequisitosEnum.CONTINUE;
  }, [requisitosDeTramites, categorias, requisitos]);

  useEffect(() => {
    setStatusRequisitos(status);
  }, [status]);

  useEffect(() => {
    if (fetchCallback) fetchCallback(categorias[0]?.requisitos || []);
  }, [categorias]);

  useEffect(() => {
    if (showMessagesRequisitos) {
      Modal.confirm({
        onOk: async () => {
          const actives = requisitosDeTramites
            .filter((requisito) => requisito.estados_globales === 2);
          const responses = await Promise.all(actives
            .map(({ id }) => patchRequisitosDeTramites(id, { estados_globales: 4 })));
          if (responses.every((e) => e)) {
            if (authorizeAllCallback) {
              authorizeAllCallback();
              setShowMessagesRequisitos(false);
            }
          } else {
            message.warn('Ha ocurrido un error al autorizar los registros, inténte de nuevo');
          }
          if (responses.some((e) => e)) {
            await fetchData();
          }
        },
        onCancel: () => setShowMessagesRequisitos(false),
        okText: 'Continuar',
        title: 'Confirmación',
        content: (
          <>
            Los requisitos en estatus
            {' '}
            <Tag color="default" style={{ margin: 0 }}>
              Cargado por funcionario
            </Tag>
            {' '}
            seran autorizados.
            <br />
            Ya no será posible realizar modificaciones, ¿desea continuar?
          </>
        ),
        okButtonProps: { className: 'no-color' },
        cancelButtonProps: { className: 'no-color' },
      });
    }
  }, [showMessagesRequisitos]);

  const infoProps = [
    {
      name: 'cantidad_de_originales',
      label: 'Cantidad de originales',
    },
    {
      name: 'cantidad_de_copias',
      label: 'Cantidad de copias',
    },
    {
      name: 'tiene_vigencia',
      label: 'Tiene vigencia',
      render: (val, record) => {
        if (val) {
          const cantidad = record.vigencia_en_dias;
          const unidad = unidadesDePlazo.find((e) => e.id === record.unidad_de_plazo);
          const separator = !(cantidad && unidad) ? ', ' : ' ';
          return `Si, ${cantidad || 'cantidad no definida'}${separator}${unidad?.descripcion || 'unidad de plazo no definida'}`;
        }
        return 'No';
      },
    },
    {
      name: 'observaciones',
      label: 'Observaciones',
    },
    {
      name: 'documentoDigitalExistente',
      label: 'Documento existente',
      cond: (record) => record.permite_documento_digital,
      render: (val) => {
        if (val) {
          return (
            <Text>
              {val.fecha_vencimiento?.format() || ''}
              <Text type={!val.vigente ? 'danger' : 'success'}>
                {` (${!val.vigente ? 'Expirado' : 'Vigente'})`}
              </Text>
              {/* <Button type="link" onClick={() => setIframeURL(val.archivo)}>
                <EyeOutlined />
                Ver documento
              </Button> */}
            </Text>
          );
        }
        return null;
      },
    },
  ];

  const renderInfoRequisito = (requisito, renderDescripcion = true) => (
    <Col
      span={renderDescripcion ? 12 : 24}
      key={`requisito_${requisito.id}`}
    >
      {renderDescripcion && (
        <>
          <Text
            style={{ fontSize: 16, textTransform: 'uppercase' }}
            className={`${requisito.requisitoDeTramite ? 'success' : ''}`}
          >
            {requisito.descripcion}
          </Text>
          <br />
        </>
      )}
      <Text className="no-bold">
        <b>
          Tipo de requisito
          {': '}
        </b>
        {requisito.tipo_de_requisito.descripcion}
      </Text>
      <br />
      {requisito.tipo_de_requisito.id === tiposDeRequisitosEnum.INCLUSIVO && requisito.related && (
        <>
          <Text className="no-bold">
            <b>
              Requisitos relacionados
              {': '}
            </b>
            {listToString(requisito.related.map((r) => r.descripcion))}
          </Text>
          <br />
        </>
      )}
      {infoProps.filter((p) => (p.cond ? p.cond(requisito) : true)).map(({
        label,
        name,
        render,
      }) => (
        <React.Fragment key={`${label}_${requisito.id}`}>
          <Text className="no-bold">
            <b>{label}</b>
            {': '}
            {(render ? render(requisito[name], requisito) : requisito[name])
              ?? `Sin ${label.toLowerCase()}`}
          </Text>
          <br />
        </React.Fragment>
      ))}
    </Col>
  );

  const getStatusTag = (_requisito) => {
    if (_requisito) {
      if (_requisito.estados_globales === ESTADOS_GLOBALES_MAP.ACTIVO) {
        if (_requisito.es_requisito_incompleto) {
          return (
            <Tag color="warning">
              En revisión por solicitante
            </Tag>
          );
        }
        return (
          <Tag color="default">
            Cargado por solicitante
          </Tag>
        );
      }
      if (_requisito.estados_globales === ESTADOS_GLOBALES_MAP.EN_PROCESO) {
        if (_requisito.es_requisito_incompleto) {
          return (
            <Tag color="warning">
              En revisión por funcionario
            </Tag>
          );
        }
        return (
          <Tag color="default">
            Cargado por funcionario
          </Tag>
        );
      }
      if (_requisito.estados_globales === ESTADOS_GLOBALES_MAP.AUTORIZADO) {
        return (
          <Tag color="success">
            Autorizado
          </Tag>
        );
      }
    }
    return null;
  };

  const Requisitos = () => (
    <RequisitosContainer>
      {categorias.map((categoria) => (
        <Col span={24} style={{ marginBottom: 25 }} key={`categoria_${categoria.id}`}>
          <Title level={4}>
            {categoria.descripcion}
          </Title>
          <Collapse ghost>
            {categoria.groups.map((_group, _idx) => {
              const group = { ..._group, id: _idx };
              if (group.tipo_de_requisito.id === tiposDeRequisitosEnum.EXCLUSIVO) {
                const requisitosToString = listToString(group.requisitos
                  .map((e) => e.descripcion), 'disjunction');
                const idxRequisitoDeTramite = group.requisitos
                  .findIndex((requisito) => requisito.requisitoDeTramite);
                const _requisito = group.requisitos[idxRequisitoDeTramite];
                if (_requisito) {
                  group.requisitos = [
                    _requisito,
                    ...group.requisitos.filter((e, idx) => idx !== idxRequisitoDeTramite),
                  ];
                }
                const items = group.requisitos.map((requisito) => {
                  const output = {
                    key: `ddmi_requisito_${requisito.id}`,
                    label: requisito.descripcion,
                    children: isLastStep || REPUVE ? [] : [
                      {
                        key: `add_requisito_${requisito.id}`,
                        onClick: () => onClickAdd(requisito, false),
                        label: 'Marcar requisito físico',
                      },
                    ],
                  };
                  if (requisito.permite_documento_electronico) {
                    output.children = [
                      ...output.children,
                      {
                        key: `add_doc_digital_requisito_${requisito.id}`,
                        onClick: () => onClickAdd(requisito, true),
                        label: 'Nuevo archivo dígital',
                      },
                    ];
                    if (requisito.documentoDigitalExistente.length) {
                      const plural = requisito.documentoDigitalExistente.length > 1;
                      output.children = [
                        ...output.children,
                        {
                          key: `link_doc_digital_requisito_${requisito.id}`,
                          onClick: () => onClickLinkDigital(requisito,
                            requisito.documentoDigitalExistente),
                          label: `Vincular documento${plural ? 's' : ''} dígital${plural ? 'es' : ''} existente${plural ? 's' : ''} ${DEV
                            ? requisito.documentoDigitalExistente.map((e) => e.id).join() : `(${requisito.documentoDigitalExistente.length})`}`,
                        },
                      ];
                    }
                  }
                  return output;
                });
                const requisitoDeTramite = _requisito?.requisitoDeTramite;
                const tag = getStatusTag(requisitoDeTramite);
                return (
                  <Collapse.Panel
                    header={(
                      <Text>
                        <span className="required-mark">*</span>
                        {requisitosToString}
                      </Text>
                    )}
                    extra={_requisito ? (
                      <>
                        <Button
                          type="link"
                          onClick={() => onClickView(_requisito)}
                        >
                          <EyeOutlined />
                          {!disabled && isInReview(requisitoDeTramite) ? 'Revisar' : 'Ver'}
                        </Button>
                        <Button
                          type="link"
                          onClick={() => onClickDelete(_requisito)}
                          disabled={!isMutable(requisitoDeTramite) || disabled}
                          className="no-color"
                          danger
                        >
                          <DeleteOutlined />
                          Eliminar
                        </Button>
                        {tag}
                      </>
                    ) : (
                      <Dropdown
                        overlay={<Menu items={items} />}
                        placement="top"
                        disabled={disabled || !items[0].children.length}
                      >
                        <Button
                          type="link"
                          onClick={screens.md ? items[0].children[0].onClick : null}
                          style={{ marginLeft: 'auto' }}
                        >
                          <PlusOutlined />
                          Agregar
                        </Button>
                      </Dropdown>
                    )}
                    key={`group_${group.id}`}
                  >
                    <Row gutter={[20, 30]}>
                      {group.requisitos.map((requisito) => renderInfoRequisito(requisito))}
                    </Row>
                  </Collapse.Panel>
                );
              }
              return group.requisitos.map((requisito) => {
                const canAdd = !requisito.requisitoDeTramite
                  && requisito.tipo_de_requisito.id !== tiposDeRequisitosEnum.DINAMICO;
                const items = isLastStep || REPUVE ? [] : [
                  {
                    key: `add_requisito_${requisito.id}`,
                    onClick: () => onClickAdd(requisito, false),
                    label: 'Marcar requisito físico',
                  },
                ];
                if (requisito.permite_documento_electronico) {
                  items.push({
                    key: `add_doc_digital_requisito_${requisito.id}`,
                    onClick: () => onClickAdd(requisito, true),
                    label: 'Nuevo archivo dígital',
                  });
                  if (requisito.documentoDigitalExistente.length) {
                    const plural = requisito.documentoDigitalExistente.length > 1;
                    items.push({
                      key: `link_doc_digital_requisito_${requisito.id}`,
                      onClick: () => onClickLinkDigital(
                        requisito,
                        requisito.documentoDigitalExistente,
                      ),
                      label: `Vincular documento${plural ? 's' : ''} dígital${plural ? 'es' : ''} existente${plural ? 's' : ''} ${DEV
                        ? requisito.documentoDigitalExistente.map((e) => e.id).join() : `(${requisito.documentoDigitalExistente.length})`}`,
                    });
                  }
                }
                const tag = getStatusTag(requisito.requisitoDeTramite);
                return (
                  <Collapse.Panel
                    header={(
                      <Text>
                        {requisito.required && (
                          <span className="required-mark">*</span>
                        )}
                        {requisito.descripcion}
                        {requisito.tipo_de_requisito.id === tiposDeRequisitosEnum.DINAMICO && (
                          <Tooltip title="Será obtenido de forma dinámica">
                            <QuestionCircleOutlined />
                          </Tooltip>
                        )}
                      </Text>
                    )}
                    extra={(
                      <>
                        {requisito.requisitoDeTramite && (
                          <>
                            <Button
                              type="link"
                              onClick={() => onClickView(requisito)}
                            >
                              <EyeOutlined />
                              {!disabled && isInReview(requisito.requisitoDeTramite) ? 'Revisar' : 'Ver'}
                            </Button>
                            <Button
                              type="link"
                              disabled={!isMutable(requisito.requisitoDeTramite) || disabled}
                              onClick={() => onClickDelete(requisito)}
                              className="no-color"
                              danger
                            >
                              <DeleteOutlined />
                              Eliminar
                            </Button>
                            {tag}
                          </>
                        )}
                        {canAdd && (
                          <Dropdown
                            overlay={<Menu items={items} />}
                            placement="top"
                            disabled={disabled || !items.length}
                          >
                            <Button
                              type="link"
                              onClick={screens.md ? items[0].onClick : null}
                              style={{ marginLeft: 'auto' }}
                            >
                              <PlusOutlined />
                              Agregar
                            </Button>
                          </Dropdown>
                        )}
                      </>
                    )}
                    key={`cp_requisito_${requisito.id}`}
                  >
                    {renderInfoRequisito(requisito, false)}
                  </Collapse.Panel>
                );
              });
            })}
          </Collapse>
        </Col>
      ))}
    </RequisitosContainer>
  );

  const onClickAuthorize = async () => {
    setLoading(true);
    const { fecha_vencimiento } = form.getFieldsValue();
    const values = {
      estados_globales: 4,
      fecha_de_vencimiento: fecha_vencimiento,
      es_requisito_incompleto: false,
    };
    const updated = isAuthorized(selectedRequisito)
      ? selectedRequisito : await patchRequisitosDeTramites(selectedRequisito.id, values);
    if (!isAuthorized(updated)) {
      return setLoading(false);
    }
    const { documentos_de_tramite } = selectedRequisito;
    let authorizedDoc = isAuthorized(documentos_de_tramite);
    if (!authorizedDoc) {
      delete values.fecha_de_vencimiento;
      delete values.es_requisito_incompleto;
      values.fecha_vencimiento = fecha_vencimiento;
      // TODO: autorizar todos en multiples
      authorizedDoc = await patchDocumentoDeTramite(documentos_de_tramite.id, values);
    }
    await fetchDocumentosParaTramites(true);
    if (authorizedDoc) {
      const requisitosNoAutorizados = _requisitosDeTramites
        .filter((r) => r.estados_globales !== ESTADOS_GLOBALES_MAP.AUTORIZADO);
      const requisitosEnReview = _requisitosDeTramites.filter(isInReview);
      if (requisitosEnReview.length === 1 && requisitosNoAutorizados.length === 1) {
        authorizeAllCallback();
      }
      onCancel();
    }
    return setLoading(false);
  };

  const onClickAddChanges = async () => setVisibleAddComments(true);

  const onSaveComments = async () => {
    setLoading(true);
    const values = form.getFieldsValue();
    values.estados_globales = 1;
    values.es_requisito_incompleto = true;
    const updated = await patchRequisitosDeTramites(selectedRequisito.id, values);
    if (updated) {
      await fetchData();
      onCancel();
    }
    setLoading(false);
  };

  const selectedInReviewStatus = !disabled && isInReview(selectedRequisito);

  const _onCancel = () => (visibleAddComments ? setVisibleAddComments(false) : onCancel());

  if (!hasPermission) {
    return <NoPermission />;
  }

  return (
    <>
      <Requisitos />
      <Modal
        visible={visible}
        footer={null}
        closable={false}
        maskClosable={false}
        keyboard={!loading}
        centered
        width={screens.md ? '50%' : null}
        title={(
          <FormSubmitCustomControls
            label={visibleAddComments ? 'Añadir Observaciones'
              : `${selectedRequisito ? ((selectedInReviewStatus && 'Revisar') || 'Ver') : 'Agregar'} Requisito de trámite`}
            controls={[
              {
                text: 'Regresar',
                icon: <ArrowLeftOutlined />,
                onClick: _onCancel,
              },
              !selectedRequisito && {
                text: 'Guardar',
                icon: <SaveOutlined />,
                onClick: form.submit,
              },
              selectedInReviewStatus && !visibleAddComments && {
                text: 'Autorizar',
                icon: <CheckCircleOutlined />,
                onClick: onClickAuthorize,
                className: 'success no-color',
              },
              selectedInReviewStatus && !visibleAddComments && {
                text: 'Solicitar cambios',
                icon: <CloseCircleOutlined />,
                onClick: onClickAddChanges,
                className: 'warn no-color',
              },
              selectedInReviewStatus && visibleAddComments && {
                text: 'Guardar',
                icon: <SaveOutlined />,
                onClick: onSaveComments,
              },
            ].filter((c) => c)}
            permission={permission}
          />
        )}
        destroyOnClose
      >
        <Spin tip="Cargando..." spinning={loading}>
          <Form
            form={form}
            layout="vertical"
            onFinish={selectedRequisito ? null : onFinish}
            initialValues={{ estados_globales: 2 }}
          >
            {visibleAddComments ? (
              <Col span={24}>
                <Form.Item
                  label="Observaciones"
                  name="observaciones"
                  rules={rules.required}
                >
                  <Input.TextArea autoSize={{ minRows: 3, maxRows: 3 }} />
                </Form.Item>
              </Col>
            ) : (
              <>
                <Form.Item
                  label="Requisito"
                  name="requisito"
                  rules={rules.required}
                >
                  <Select disabled dataSource={requisitos} />
                </Form.Item>
                {(selectedRequisito?.documentos_de_tramite?.archivo
                  || selectedRequisito?.documentos_de_tramite?.length) && (
                  <>
                    <Form.Item
                      label={(
                        <>
                          {selectedRequisito.documentos_de_tramite?.archivo && (
                            <>
                              Documento Dígital
                              <Tooltip title={selectedRequisito.documentos_de_tramite?.archivo}>
                                <LinkOutlined />
                              </Tooltip>
                            </>
                          )}
                          {selectedRequisito?.documentos_de_tramite?.length
                            && `Documentos Digitales (${selectedRequisito?.documentos_de_tramite?.length})`}
                        </>
                      )}
                    >
                      <Previewer
                        iFrameUrl={selectedRequisito.documentos_de_tramite?.archivo
                          ? selectedRequisito.documentos_de_tramite.archivo
                          : selectedRequisito.documentos_de_tramite.map((d) => d.archivo)}
                        ifameHeight="50vh"
                        modal={false}
                        visible
                      />
                    </Form.Item>
                  </>
                )}
                {!selectedRequisito && es_documento_digital && (
                  <Camera
                    uploadMode="uploader"
                    defualtMode={/fotograf[ií]a/i.test(requisitoSelected?.descripcion)
                      ? 'capture' : 'upload'}
                    multiple={requisitoSelected?.permite_multiples_documentos}
                  />
                )}
                <Form.Item
                  name="fecha_vencimiento"
                  label="Fecha de Vencimiento"
                  rules={tiene_vigencia ? rules.required : []}
                >
                  <DatePicker
                    placeholder={selectedRequisito ? 'Sin fecha de vencimiento' : ''}
                    format={DATE_FRONTEND_FORMAT}
                    disabled={selectedRequisito && !selectedInReviewStatus
                      && !selectedRequisito.fecha_vencimiento}
                    disabledDate={disabledDate}
                  />
                </Form.Item>
                <Form.Item label="Estado" name="estados_globales">
                  <Select dataSource={estadosGlobales} disabled />
                </Form.Item>
                {selectedRequisito?.observaciones && (
                  <Form.Item
                    label="Observaciones"
                    name="observaciones"
                    rules={rules.required}
                  >
                    <Input.TextArea disabled autoSize={{ minRows: 3, maxRows: 3 }} />
                  </Form.Item>
                )}
                <Form.Item hidden name="tiene_vigencia">
                  <Select trueFalse />
                </Form.Item>
                <Form.Item hidden={!selectedRequisito} label="Fecha alta" name="fecha_de_creacion">
                  <Input disabled />
                </Form.Item>
                <Form.Item hidden={!selectedRequisito} label="Usuario alta" name="usuario_creo">
                  <Input disabled />
                </Form.Item>
                <Form.Item hidden name="es_documento_digital">
                  <Select trueFalse />
                </Form.Item>
                <Form.Item hidden name="id">
                  <Input />
                </Form.Item>
                <Form.Item hidden>
                  <Button htmlType="submit" />
                </Form.Item>
              </>
            )}
          </Form>
        </Spin>
      </Modal>
      <ModalDelete
        onDelete={deleteItem}
        onCancel={() => setVisibleAlert(false)}
        visible={visibleAlert}
        loading={loading}
        content={`el Requisito ${selectedRequisito?.descripcion.replace(/\d+$/, '')}`}
      />
    </>
  );
};

RequisitosDeTramite.propTypes = {
  permission: PropTypes.shape({}).isRequired,
  casoDeRequisitos: PropTypes.shape({
    id: PropTypes.number,
    requisitos: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number })),
  }),
  tramite: PropTypes.shape({
    id: PropTypes.number,
    caso: PropTypes.number,
    solicitante_ciudadano: PropTypes.bool,
    ciudadano: PropTypes.number,
    contribuyente: PropTypes.number,
    content_type_a_padron: PropTypes.number,
    object_id_padron: PropTypes.number,
    canal_de_pago: PropTypes.number,
  }),
  setStatusRequisitos: PropTypes.func.isRequired,
  setCasoDeRequisitos: PropTypes.func,
  loading: PropTypes.bool,
  setLoading: PropTypes.func.isRequired,
  setShowMessagesRequisitos: PropTypes.func,
  showMessagesRequisitos: PropTypes.bool,
  requisitosGenerales: PropTypes.arrayOf(PropTypes.oneOfType([
    PropTypes.shape({ id: PropTypes.number }), PropTypes.number])),
  disabled: PropTypes.bool,
  allowReview: PropTypes.bool,
  hasPermission: PropTypes.bool,
  authorizeAllCallback: PropTypes.func,
  isLastStep: PropTypes.bool,
  fetchCallback: PropTypes.func,
};

RequisitosDeTramite.defaultProps = {
  loading: false,
  casoDeRequisitos: null,
  tramite: null,
  showMessagesRequisitos: false,
  setShowMessagesRequisitos: null,
  requisitosGenerales: null,
  setCasoDeRequisitos: null,
  disabled: false,
  allowReview: false,
  hasPermission: false,
  authorizeAllCallback: null,
  isLastStep: false,
  fetchCallback: null,
};

const RequisitosContainer = styled.div`
  width: 100%;
  height: 100%;
  .required-mark {
    display: inline-block;
    margin-right: 4px;
    color: #ff4d4f;
    font-size: 14px;
    line-height: 1;
    font-weight: bold;
  }
  .no-bold {
    font-weight: normal !important;;
  }
  .ant-typography .anticon {
    margin-left: 5px;
  }
`;

export default RequisitosDeTramite;
